'use client'

import React, { useState, useCallback, memo, useEffect, useRef } from "react"
import { useAuth, useUser } from '@clerk/clerk-react';
import { Button, FormControlLabel, Switch, TextField, Select, MenuItem, Dialog, DialogActions, DialogContent, DialogTitle, Box, Typography, Grid, Avatar, Paper, Chip, useTheme, IconButton, Tooltip, Autocomplete, InputAdornment, Divider, Menu, Snackbar, Alert, Slider, InputLabel, FormControl, CircularProgress, Checkbox, FormGroup, LinearProgress } from '@mui/material'
import { Book as BookOpen, ChevronLeft, ChevronRight, Share as Share2, VolumeUp as Volume2, Add as AddIcon, Remove as RemoveIcon, Info as InfoIcon, MoreHoriz as MoreHorizIcon, Twitter as TwitterIcon, MailOutline as MailOutlineIcon, Casino as CasinoIcon, ContentCopy as ContentCopyIcon, Close as CloseIcon, Facebook as FacebookIcon, LinkedIn as LinkedInIcon } from '@mui/icons-material'
import { Sparkles, User, Image as ImageIcon, Plus, MinusCircle, AlertCircle as LucideAlertCircle } from 'lucide-react'
import MultiSelectCreatable from "../ui/MultiSelectCreatable.js"
import { v4 as uuidv4 } from 'uuid'
import { shuffle } from '../../utils/arrayUtils.js';
import { themeOptions, settingOptions, styleOptions } from '../../utils/storyOptions.js';
import { imageModelOptions } from '../../utils/imageModelOptions.js';
import { db } from "../../lib/firebase.js";
import { collection, setDoc, updateDoc, doc } from "firebase/firestore";
import ShareDialog from '../ShareDialog/ShareDialog.js';
import StoryAudioPlayer from '../AudioPlayer/StoryAudioPlayer.js'; // Import the new audio player
import { generateStory } from '../../utils/storyGeneration.js';
import { generateAudio } from '../../services/ttsService.js';
import { generateStoryImages, generateCoverImage } from '../../services/imageService.js';
import { Storybook3DReader } from '../Storybook/index.js';
import { createTheme, ThemeProvider } from "@mui/material/styles/index.js";
import { useToast } from "../../hooks/use-toast.jsx";
import { useSearchParams } from "react-router-dom";
import { useSubscription } from '../../contexts/SubscriptionContext.js';

const STORAGE_OPERATION_TIMEOUT_MS = 30000;

const withStorageTimeout = (promise, operation) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(
    () => reject(new Error(`${operation} timed out. Please try saving again.`)),
    STORAGE_OPERATION_TIMEOUT_MS
  );
  promise.then(
    (value) => {
      clearTimeout(timeoutId);
      resolve(value);
    },
    (error) => {
      clearTimeout(timeoutId);
      reject(error);
    }
  );
});

const persistStoryAssets = async (pages, coverImageUrl, userId, storyId, authToken) => {
  const uploadDataUrl = async (dataUrl, fileName) => {
    if (!dataUrl || !String(dataUrl).startsWith('data:')) return dataUrl || null;
    const response = await withStorageTimeout(fetch('/api/upload-storage-asset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ storyId, fileName, dataUrl }),
    }), `Uploading ${fileName}`);
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.downloadUrl) throw new Error(result.error || `Could not store ${fileName}`);
    return result.downloadUrl;
  };

  const persistedPages = await Promise.all((pages || []).map(async (page, index) => ({
    ...page,
    imageUrl: await uploadDataUrl(page.imageUrl, `page-${index + 1}.png`),
  })));
  const persistedCover = await uploadDataUrl(coverImageUrl, 'cover.png');
  return { pages: persistedPages, coverImageUrl: persistedCover };
};

// --- Character Form Categories (matching Characters.js) ---

const traitCategories = [
  { label: 'Personality (Positive)', options: ['Kind', 'Brave', 'Curious', 'Funny', 'Adventurous', 'Creative', 'Logical', 'Patient', 'Determined', 'Optimistic', 'Generous', 'Loyal', 'Honest', 'Resourceful', 'Adaptable', 'Confident', 'Empathetic', 'Charming', 'Wise', 'Humble'] },
  { label: 'Personality (Neutral/Situational)', options: ['Shy', 'Quiet', 'Serious', 'Reserved', 'Skeptical', 'Dreamy', 'Impulsive', 'Competitive', 'Practical', 'Idealistic', 'Cautious', 'Spontaneous', 'Methodical', 'Whimsical'] },
  { label: 'Personality (Negative)', options: ['Grumpy', 'Pessimistic', 'Stubborn', 'Arrogant', 'Impatient', 'Lazy', 'Greedy', 'Jealous', 'Vain', 'Anxious', 'Bossy', 'Clumsy', 'Forgetful', 'Naive'] },
  { label: 'Skills/Aptitudes', options: ['Intelligent', 'Athletic', 'Artistic', 'Musical', 'Tech-savvy', 'Good Listener', 'Problem Solver', 'Organized', 'Persuasive', 'Observant'] },
];

const abilityCategories = [
  { label: 'Physical', options: ['Super Speed', 'Super Strength', 'Flight', 'Enhanced Agility', 'Enhanced Senses', 'Wall-Crawling', 'Regeneration', 'Invisibility', 'Shape-shifting'] },
  { label: 'Mental/Psychic', options: ['Telekinesis', 'Telepathy', 'Mind Control', 'Precognition', 'Illusion Casting', 'Memory Manipulation', 'Rapid Learning'] },
  { label: 'Elemental/Nature', options: ['Control Water', 'Control Fire', 'Control Earth', 'Control Air', 'Control Weather', 'Talking to Animals', 'Plant Manipulation'] },
  { label: 'Magic/Energy', options: ['Spell Casting', 'Energy Blasts', 'Healing', 'Force Fields', 'Teleportation', 'Summoning', 'Alchemy'] },
  { label: 'Technology/Gadgets', options: ['Gadgeteer', 'Hacking', 'Powered Armor', 'Robotics Expert', 'Weapon Master'] },
  { label: 'Other/Unique', options: ['Luck Manipulation', 'Reality Warping (minor)', 'Fourth-Wall Awareness', 'Cartoon Physics', 'Musical Ability (Magical)', 'Dream Walking'] },
];

const likeCategories = [
  { label: 'Hobbies/Activities', options: ['Reading', 'Playing Outside', 'Drawing', 'Singing', 'Building', 'Puzzles', 'Games (Video)', 'Games (Board)', 'Sports', 'Dancing', 'Cooking/Baking', 'Gardening', 'Crafting', 'Collecting', 'Stargazing'] },
  { label: 'Social', options: ['Helping Others', 'Making Friends', 'Family Time', 'Parties', 'Teamwork', 'Sharing', 'Talking', 'Listening to Stories'] },
  { label: 'Sensory/Food', options: ['Music', 'Sweet Treats', 'Sunny Days', 'Rainy Days', 'Soft Blankets', 'Hugs', 'Nature Sounds', 'Favorite Foods', 'Smell of Cookies'] },
  { label: 'Learning/Thinking', options: ['Learning New Things', 'Solving Problems', 'Exploring', 'Asking Questions', 'Daydreaming', 'Science Experiments'] },
  { label: 'Animals/Nature', options: ['Animals (General)', 'Puppies', 'Kittens', 'Forests', 'Oceans', 'Flowers', 'Bugs (Friendly)'] },
];

const dislikeCategories = [
  { label: 'Sensory/Food', options: ['Loud Noises', 'Bright Lights', 'Bad Smells', 'Being Cold', 'Being Hot', 'Scratchy Clothes', 'Vegetables', 'Spicy Food', 'Bitter Flavors'] },
  { label: 'Situations', options: ['Being Alone', 'Waking Up Early', 'Bedtime', 'Chores', 'Waiting', 'Being Rushed', 'Doctor Visits', 'Getting Lost', 'Arguments', 'Tests'] },
  { label: 'Social', options: ['Bullies', 'Being Teased', 'Sharing (Sometimes)', 'Being Ignored', 'Large Crowds', 'Being the Center of Attention'] },
  { label: 'Phobias/Creatures', options: ['Spiders', 'The Dark', 'Monsters', 'Ghosts', 'Snakes', 'Insects (Scary)'] },
  { label: 'Abstract', options: ['Messiness', 'Unfairness', 'Rules (Sometimes)', 'Change', 'Surprises (Bad)'] },
];

const fearCategories = [
  { label: 'Common Phobias', options: ['Spiders (Arachnophobia)', 'Heights (Acrophobia)', 'The Dark (Nyctophobia)', 'Snakes (Ophidiophobia)', 'Dogs (Cynophobia)', 'Enclosed Spaces (Claustrophobia)', 'Water (Aquaphobia)', 'Needles (Trypanophobia)', 'Thunder/Lightning (Astraphobia)', 'Clowns (Coulrophobia)'] },
  { label: 'Social/Emotional Fears', options: ['Being Alone (Monophobia)', 'Public Speaking (Glossophobia)', 'Failure (Atychiphobia)', 'Being Judged', 'Rejection', 'Making Mistakes', 'Losing Loved Ones', 'Change (Metathesiophobia)', 'Conflict', 'Embarrassment'] },
  { label: 'Abstract/Existential Fears', options: ['The Unknown', 'The Future', 'Monsters Under the Bed', 'Nightmares', 'Loud Noises (Ligyrophobia)', 'Getting Lost', 'Death (Thanatophobia)', 'Being Forgotten'] },
  { label: 'Situational Fears', options: ['Doctors/Dentists (Iatrophobia/Dentophobia)', 'Flying (Aerophobia)', 'Tests/Exams', 'Large Crowds (Agoraphobia/Enochlophobia)', 'Germs (Mysophobia)', 'Being Trapped'] }
];

const dreamCategories = [
  { label: 'Adventure/Exploration', options: ['Flying Freely', 'Exploring Space/Planets', 'Sailing the High Seas', 'Discovering Lost Civilizations', 'Time Traveling', 'Climbing the Tallest Mountain', 'Journey Through a Magical Forest', 'Deep Sea Diving', 'Archaeological Digs'] },
  { label: 'Skill/Achievement', options: ['Becoming a Renowned Hero', 'Mastering Ancient Magic', 'Becoming a Famous Musician/Artist', 'Winning the Grand Championship', 'Building an Invention that Changes the World', 'Solving an Unsolvable Riddle', 'Writing a Bestselling Book', 'Graduating with Honors'] },
  { label: 'Relationships/Community', options: ['Making Lifelong Friends', 'Finding True Love', 'Helping the Needy', 'Having a Big, Happy Family', 'Leading a Successful Team', 'Bringing Peace to Warring Factions', 'Creating a Welcoming Community', 'Being Understood'] },
  { label: 'Fantasy/Wonder', options: ['Talking to Animals Fluently', 'Having Versatile Superpowers', 'Living in a Magnificent Castle', 'Befriending Dragons/Unicorns', 'Finding Legendary Treasure', 'Having an Endless Supply of Favorite Things', 'Walking on Clouds', 'Visiting Dream Worlds'] },
  { label: 'Personal Growth', options: ['Overcoming Fears', 'Becoming Confident', 'Finding Inner Peace', 'Learning About Oneself', 'Achieving Enlightenment', 'Making a Difference'] },
];

const animalOptions = [
  'Dog', 'Cat', 'Bird', 'Fish', 'Hamster', 'Rabbit', 'Guinea Pig', 'Ferret', 'Turtle', 'Lizard', 'Snake', 'Horse', 'Frog', 'Mouse', 'Rat', 'Other'
];

// Helper function to convert Data URL to Blob
function dataURItoBlob(dataURI) {
  if (!dataURI || !dataURI.includes(',')) {
    console.error("Invalid data URI provided to dataURItoBlob:", dataURI ? dataURI.substring(0, 50) + '...' : 'null');
    return null;
  }
  try {
    let base64 = dataURI.split(',')[1];
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  } catch (error) {
    console.error("Error converting data URI to Blob:", error, dataURI.substring(0, 50) + '...');
    return null;
  }
}

// Define slider marks and mapping
const lengthMarks = [
  { value: 0, label: 'Short' },
  { value: 1, label: 'Medium' },
  { value: 2, label: 'Long' },
];

function valueToLengthText(value) {
  return lengthMarks.find(mark => mark.value === value)?.label || 'Medium';
}

// Helper to safely convert value to string array - stricter handling
const ensureArray = (value) => {
  if (Array.isArray(value)) {
      return value.map(item => String(item)); 
  }
  if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
          try {
              const parsed = JSON.parse(trimmedValue);
              return Array.isArray(parsed) ? parsed.map(item => String(item)) : [];
          } catch (e) {
              console.warn('[StoryWizard] Failed to parse string as JSON array:', value, e);
              return [];
          }
      } else {
          console.warn('[StoryWizard] Received non-JSON string where array expected:', value);
          return []; 
      }
  }
  return [];
}

// ADDED: Detailed voice options
const availableVoices = [
  { id: 'alloy', name: 'Alloy', gender: 'Female', description: 'A clear and versatile voice.' },
  { id: 'echo', name: 'Echo', gender: 'Male', description: 'A resonant and engaging voice.' },
  { id: 'fable', name: 'Fable', gender: 'Female', description: 'A warm and expressive storyteller.' },
  { id: 'onyx', name: 'Onyx', gender: 'Male', description: 'A deep and authoritative voice.' },
  { id: 'nova', name: 'Nova', gender: 'Female', description: 'A bright and energetic voice.' },
];

const NewCharacterDialog = memo(({ isOpen, onClose, onSubmit, newCharacter, onNewCharacterChange, toast, getToken, isAdmin }) => {
  const [formErrors, setFormErrors] = useState({});
  const [avatarPrompt, setAvatarPrompt] = useState('');
  const [generatedAvatar, setGeneratedAvatar] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Helper to ensure array format
  const ensureArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim()) return [value];
    return [];
  };

  const handleInputChange = (name, value) => {
    onNewCharacterChange({ target: { name, value } });
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleGenerateAvatar = async () => {
    if (!avatarPrompt || isGenerating) return;

    setIsGenerating(true);
    setGenerationError(null);
    setGeneratedAvatar(null);

    try {
      const authToken = await getToken?.();
      const response = await fetch('/api/generate-avatar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        body: JSON.stringify({ prompt: avatarPrompt }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || `Generation failed (${response.status})`);
      }

      if (data.image_url) {
        setGeneratedAvatar(data.image_url);
        setGenerationError(null);
      } else {
        throw new Error('No image returned from server');
      }
    } catch (error) {
      console.error('Avatar generation error:', error);
      setGenerationError(error.message);
      setGeneratedAvatar('error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUseAvatar = async () => {
    if (!generatedAvatar || generatedAvatar === 'error') {
      toast?.({
        title: "No Avatar Available",
        description: "No valid generated avatar to use.",
        variant: "destructive",
      });
      return;
    }

    const avatarBlob = dataURItoBlob(generatedAvatar);

    if (!avatarBlob) {
      toast?.({
        title: "Processing Error",
        description: "Failed to process the generated image. Please try generating again.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);

    try {
      const fileExt = avatarBlob.type.split('/')[1] || 'png';
      const authToken = await getToken?.();
      const response = await fetch('/api/upload-storage-asset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          fileName: `${uuidv4()}.${fileExt}`,
          dataUrl: generatedAvatar,
          assetType: 'avatar',
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Avatar storage is unavailable.');
      const publicUrl = result.downloadUrl;

      if (publicUrl) {
        handleInputChange('avatar', publicUrl);
        setGeneratedAvatar(null);
        setAvatarPrompt("");
        toast?.({
          title: "Avatar Uploaded",
          description: "Your avatar has been saved!",
        });
      } else {
        throw new Error("Upload worked, but couldn't retrieve the image URL.");
      }
    } catch (error) {
      console.error("Error in handleUseAvatar during upload:", error);
      toast?.({
        title: "Avatar Upload Failed",
        description: `Failed to finalize avatar: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = () => {
    const errors = {};
    if (!newCharacter.name) errors.name = 'Name is required';
    if (!newCharacter.age) errors.age = 'Age is required';
    if (!newCharacter.gender) errors.gender = 'Gender is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    onSubmit();
  };

  const handleClose = () => {
    setFormErrors({});
    setAvatarPrompt('');
    setGeneratedAvatar(null);
    setIsGenerating(false);
    setGenerationError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', color: '#4338ca' }}>
          Create New Character
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 3, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Avatar Section - Admin Only */}
          {isAdmin && (
            <Box sx={{ borderBottom: '1px solid #e8e8f0', pb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 2 }}>Avatar Image</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 120, height: 120, borderRadius: '50%', overflow: 'hidden',
                  border: '4px solid #a855f7', boxShadow: 3, bgcolor: '#f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {isGenerating && !generatedAvatar ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>Generating...</Typography>
                    </Box>
                  ) : generatedAvatar && generatedAvatar !== 'error' ? (
                    <img src={generatedAvatar} alt="Generated Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : newCharacter.avatar ? (
                    <img src={newCharacter.avatar} alt="Character Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <User style={{ width: 60, height: 60, color: '#9ca3af' }} />
                  )}
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Generation Prompt"
                  placeholder="e.g., Happy child with red hair"
                  value={avatarPrompt}
                  onChange={(e) => setAvatarPrompt(e.target.value)}
                  disabled={isGenerating}
                />
                {generationError && (
                  <Typography color="error" variant="caption">{generationError}</Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGenerateAvatar}
                    disabled={isGenerating || !avatarPrompt}
                    startIcon={isGenerating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Sparkles size={16} />}
                    sx={{ bgcolor: '#a855f7', '&:hover': { bgcolor: '#9333ea' } }}
                  >
                    {generatedAvatar && generatedAvatar !== 'error' ? 'Regenerate' : 'Generate'}
                  </Button>
                  {generatedAvatar && generatedAvatar !== 'error' && (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleUseAvatar}
                      disabled={isUploading}
                      startIcon={isUploading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ImageIcon size={16} />}
                      sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
                    >
                      Use Avatar
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Basic Info */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca' }}>Basic Information</Typography>
            <TextField
              fullWidth
              required
              size="small"
              label="Name"
              value={newCharacter.name || ''}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Age"
                value={newCharacter.age || ''}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value) || '')}
                error={!!formErrors.age}
                helperText={formErrors.age}
                InputProps={{ inputProps: { min: 0, max: 120 } }}
              />
              <TextField
                fullWidth
                required
                select
                size="small"
                label="Gender"
                value={newCharacter.gender || ''}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                error={!!formErrors.gender}
                helperText={formErrors.gender}
              >
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={newCharacter.isAnimal || false}
                onChange={(e) => {
                  handleInputChange('isAnimal', e.target.checked);
                  if (!e.target.checked) handleInputChange('animalType', '');
                }}
                size="small"
              />
              <Typography variant="body2">Is Animal?</Typography>
            </Box>
            {newCharacter.isAnimal && (
              <TextField
                fullWidth
                select
                size="small"
                label="Animal Type"
                value={newCharacter.animalType || ''}
                onChange={(e) => handleInputChange('animalType', e.target.value)}
              >
                {animalOptions.map(animal => (
                  <MenuItem key={animal} value={animal}>{animal}</MenuItem>
                ))}
              </TextField>
            )}
          </Box>

          {/* Traits */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Traits</Typography>
            <MultiSelectCreatable
              optionGroups={traitCategories}
              selectedOptions={ensureArray(newCharacter.traits)}
              onChange={(selected) => handleInputChange('traits', selected)}
              placeholder="Select or add traits..."
              creatablePlaceholder="Add new trait..."
              label="Character Traits"
            />
          </Box>

          {/* Special Abilities */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Special Abilities</Typography>
            <MultiSelectCreatable
              optionGroups={abilityCategories}
              selectedOptions={ensureArray(newCharacter.specialAbility)}
              onChange={(selected) => handleInputChange('specialAbility', selected)}
              placeholder="Select or add abilities..."
              creatablePlaceholder="Add new ability..."
              label="Special Abilities"
            />
          </Box>

          {/* Likes */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Likes</Typography>
            <MultiSelectCreatable
              optionGroups={likeCategories}
              selectedOptions={ensureArray(newCharacter.likes)}
              onChange={(selected) => handleInputChange('likes', selected)}
              placeholder="Select or add likes..."
              creatablePlaceholder="Add new like..."
              label="Character Likes"
            />
          </Box>

          {/* Dislikes */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Dislikes</Typography>
            <MultiSelectCreatable
              optionGroups={dislikeCategories}
              selectedOptions={ensureArray(newCharacter.dislikes)}
              onChange={(selected) => handleInputChange('dislikes', selected)}
              placeholder="Select or add dislikes..."
              creatablePlaceholder="Add new dislike..."
              label="Character Dislikes"
            />
          </Box>

          {/* Fears */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Fears</Typography>
            <MultiSelectCreatable
              optionGroups={fearCategories}
              selectedOptions={ensureArray(newCharacter.fears)}
              onChange={(selected) => handleInputChange('fears', selected)}
              placeholder="Select or add fears..."
              creatablePlaceholder="Add new fear..."
              label="Character Fears"
            />
          </Box>

          {/* Dreams */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Dreams</Typography>
            <MultiSelectCreatable
              optionGroups={dreamCategories}
              selectedOptions={ensureArray(newCharacter.dreams)}
              onChange={(selected) => handleInputChange('dreams', selected)}
              placeholder="Select or add dreams..."
              creatablePlaceholder="Add new dream..."
              label="Character Dreams"
            />
          </Box>

          {/* Backstory */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Backstory</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              placeholder="Enter character backstory..."
              value={newCharacter.backstory || ''}
              onChange={(e) => handleInputChange('backstory', e.target.value)}
            />
          </Box>

          {/* Appearance */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#4338ca', mb: 1 }}>Appearance</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              placeholder="Describe character appearance..."
              value={newCharacter.appearance || ''}
              onChange={(e) => handleInputChange('appearance', e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ color: '#6b7280', borderColor: '#d1d5db' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isGenerating || isUploading}
          sx={{ bgcolor: '#4338ca', '&:hover': { bgcolor: '#3730a3' } }}
        >
          Create Character
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// Add this near the top of the file, before the component
const theme = createTheme({
  components: {
    MuiSlider: {
      styleOverrides: {
        root: {
          height: 4,
          padding: "15px 0",
        },
        thumb: {
          height: 16,
          width: 16,
          backgroundColor: "#000",
          "&:focus, &:hover, &.Mui-active": {
            boxShadow: "0 0 0 8px rgba(0, 0, 0, 0.16)",
          },
        },
        track: {
          height: 4,
          backgroundColor: "#000",
        },
        rail: {
          height: 4,
          backgroundColor: "#e4e4e7",
        },
        mark: {
          backgroundColor: "#e4e4e7",
          height: 8,
          width: 1,
          marginTop: -2,
        },
        markActive: {
          backgroundColor: "#000",
        },
      },
    },
  },
});

export default function StoryWizard({ userTier, canUseAudio = false, characters, addCharacter, user }) {
  const theme = useTheme();
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const { isAdmin: serverIsAdmin } = useSubscription();
  const isAdmin = serverIsAdmin;
  const authorName = clerkUser?.fullName
    || clerkUser?.username
    || user?.fullName
    || user?.username
    || 'Make It Mindful';
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStep = (() => {
    const raw = parseInt(searchParams.get('step') || '0', 10);
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
  })();
  const [step, setStep] = useState(initialStep)
  const [generationStage, setGenerationStage] = useState('idle')
  const generationAbortRef = useRef(null)

  // Mirror the current step to the URL so refresh / back-button keeps wizard state.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (step === 0) {
      params.delete('step');
    } else {
      params.set('step', String(step));
    }
    setSearchParams(params, { replace: true });
    // searchParams and setSearchParams are intentionally omitted; we only want this
    // effect to fire when `step` changes.
  }, [step]); // eslint-disable-line
  const [inputs, setInputs] = useState({
    mainCharacter: '',
    otherCharacters: [],
    storyThemes: [],
    storySetting: null,
    storytellingStyle: null,
    storyLength: 1,
    storyDetails: '',
    audioEnabled: false,
    selectedVoice: 'alloy', // Default voice
    selectedImageModel: null,
  })
  const [newCharacter, setNewCharacter] = useState({
    name: "",
    age: "",
    gender: "",
    avatar: null,
    traits: [],
    specialAbility: [],
    likes: [],
    dislikes: [],
    fears: [],
    dreams: [],
    backstory: "",
    appearance: "",
    isAnimal: false,
    animalType: "",
  })
  const [generatedStory, setGeneratedStory] = useState("")
  const [generatedTitle, setGeneratedTitle] = useState("")
  const [generatedPages, setGeneratedPages] = useState([])
  const [coverImageUrl, setCoverImageUrl] = useState(null)
  const [isGeneratingImages, setIsGeneratingImages] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [detailSuggestions, setDetailSuggestions] = useState([])
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false)
  const [isSuggestionDialogOpen, setIsSuggestionDialogOpen] = useState(false)
  const [isGeneratingStory, setIsGeneratingStory] = useState(false)
  const [displayedThemeOptions, setDisplayedThemeOptions] = useState(themeOptions.slice(0, 5));
  const [displayedSettingOptions, setDisplayedSettingOptions] = useState(settingOptions.slice(0, 5));
  const [displayedStyleOptions, setDisplayedStyleOptions] = useState(styleOptions.slice(0, 5));
  const [isSavingStory, setIsSavingStory] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [savedStoryId, setSavedStoryId] = useState(null);
  const savedStoryIdRef = useRef(null);
  const [pageAudioUrls, setPageAudioUrls] = useState({});
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false); // ADDED for voice preview
  const [previewError, setPreviewError] = useState(null); // ADDED for voice preview
  const [previewLoadingVoiceId, setPreviewLoadingVoiceId] = useState(null); // ADDED to track which voice preview is loading
  const audioPreviewRef = useRef(null); // ADDED for voice preview

  const hasMainCharacter = Boolean(inputs.mainCharacter) && characters.some((character) => character.id === inputs.mainCharacter);
  const hasStoryDetails = inputs.storyThemes.length > 0 && inputs.storySetting && inputs.storytellingStyle;
  const firstIncompleteStep = !hasMainCharacter
    ? 0
    : !hasStoryDetails && step >= 3
      ? 2
      : !inputs.selectedImageModel && step >= 4
        ? 3
        : null;

  // A copied or stale wizard URL must never expose a Review screen with
  // missing required fields. Step state is only meaningful while its form
  // prerequisites still exist in the current session.
  useEffect(() => {
    if (firstIncompleteStep !== null && step > firstIncompleteStep) {
      setStep(firstIncompleteStep);
    }
  }, [firstIncompleteStep, step]);

  const { toast } = useToast();

  // Function to get random options
  const getRandomOptions = (options, count) => {
    return shuffle([...options]).slice(0, count);
  };

  // Load initial options
  useEffect(() => {
    setDisplayedThemeOptions(themeOptions.slice(0, 5));
    setDisplayedSettingOptions(settingOptions.slice(0, 5));
    setDisplayedStyleOptions(styleOptions.slice(0, 5));
  }, []);

  // Functions to load more options
  const loadMoreOptions = (currentOptions, allOptions, setOptions) => {
    const remainingOptions = allOptions.filter(option => !currentOptions.includes(option));
    const newOptions = getRandomOptions(remainingOptions, 5);
    setOptions([...currentOptions, ...newOptions]);
  };

  const handleInputChange = useCallback((name, value) => {
    setInputs(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleMainCharacterSelect = useCallback((characterId) => {
    setInputs(prev => ({
      ...prev,
      mainCharacter: characterId,
      otherCharacters: prev.otherCharacters.filter(id => id !== characterId)
    }))
  }, [])

  const handleOtherCharacterSelect = useCallback((characterId) => {
    setInputs(prev => ({
      ...prev,
      otherCharacters: prev.otherCharacters.includes(characterId)
        ? prev.otherCharacters.filter(id => id !== characterId)
        : [...prev.otherCharacters, characterId]
    }))
  }, [])

  const handleNewCharacterChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewCharacter(prev => {
      if (value === undefined) {
        const { [name]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [name]: value };
    });
  }, []);

  const handleNewCharacterSubmit = useCallback(() => {
    if (newCharacter.name && newCharacter.age && newCharacter.gender) {
      addCharacter(newCharacter);
      setNewCharacter({
        name: "",
        age: "",
        gender: "",
        avatar: null,
        traits: [],
        specialAbility: [],
        likes: [],
        dislikes: [],
        fears: [],
        dreams: [],
        backstory: "",
        appearance: "",
        isAnimal: false,
        animalType: "",
      });
      setIsDialogOpen(false);
    }
  }, [newCharacter, addCharacter]);

  const generateDetailSuggestions = async () => {
    setIsGeneratingSuggestions(true);
    try {
      const authToken = await getToken();
      const response = await fetch('/api/generate-suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate suggestions');
      }

      const data = await response.json();
      const validSuggestions = data.suggestions;

      if (!validSuggestions || validSuggestions.length < 3) {
        throw new Error('Invalid number of suggestions generated');
      }

      setDetailSuggestions(validSuggestions.slice(0, 3));
      setIsSuggestionDialogOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate suggestions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingSuggestions(false);
    }
  };

  const createDraftStory = async ({ title, content, pages, mainCharacter }) => {
    if (!user?.id || !content) return null;

    try {
      const storyRef = doc(collection(db, 'stories'));
      const createdAt = new Date().toISOString();
      await setDoc(storyRef, {
        user_id: user.id,
        author_name: authorName,
        title: title || 'Untitled Story',
        story_content: content,
        pages: pages?.length ? pages : null,
        cover_image_url: null,
        themes: inputs.storyThemes,
        setting: inputs.storySetting,
        style: inputs.storytellingStyle,
        custom_details: inputs.storyDetails,
        selected_voice: inputs.selectedVoice,
        audio_page_urls: pageAudioUrls,
        image_model: inputs.selectedImageModel,
        main_character_id: mainCharacter?.id || null,
        is_public: false,
        generation_status: pages?.length && inputs.selectedImageModel !== 'none' ? 'draft' : 'complete',
        images_status: pages?.length && inputs.selectedImageModel !== 'none' ? 'generating' : 'disabled',
        created_at: createdAt,
        updated_at: createdAt,
      });

      setSavedStoryId(storyRef.id);
      savedStoryIdRef.current = storyRef.id;
      setSaveSuccess(true);
      toast({
        title: 'Story added to your shelf',
        description: 'You can read it now while the illustrations finish.',
      });
      return storyRef.id;
    } catch (error) {
      console.warn('[story-wizard] Could not create live shelf draft:', error);
      setSaveError('Your story is ready to read here, but could not be added to the shelf yet.');
      return null;
    }
  };

  const handlePageAudioStored = useCallback(async (voice, pageNumber, downloadUrl) => {
    if (!voice || !pageNumber || !downloadUrl) return;
    const nextPageAudioUrls = {
      ...pageAudioUrls,
      [voice]: { ...(pageAudioUrls[voice] || {}), [pageNumber]: downloadUrl },
    };
    setPageAudioUrls(nextPageAudioUrls);
    const storyId = savedStoryIdRef.current;
    if (storyId) {
      try {
        await updateDoc(doc(db, 'stories', storyId), { audio_page_urls: nextPageAudioUrls });
      } catch (error) {
        console.warn('[story-wizard] Could not persist page narration:', error.message);
      }
    }
  }, [pageAudioUrls]);

  const handleGenerateStory = async () => {
    if (firstIncompleteStep !== null) {
      setStep(firstIncompleteStep);
      toast({
        title: 'Complete the story setup first',
        description: firstIncompleteStep === 0
          ? 'Choose a main character before generating.'
          : firstIncompleteStep === 2
            ? 'Choose a theme, setting, and storytelling style before generating.'
            : 'Choose Text only, Fast illustrations, or Rich illustrations before generating.',
        variant: 'destructive',
      });
      return;
    }

    setIsGeneratingStory(true);
    setGenerationStage('text');
    setSaveSuccess(false);
    setSaveError(null);
    setSavedStoryId(null);
    setGeneratedTitle("");
    setGeneratedStory("");
    setGeneratedPages([]);
    setCoverImageUrl(null);
    setPageAudioUrls({});

    const mainCharacter = characters.find(c => c.id === inputs.mainCharacter);
    const otherCharacters = characters.filter(c => inputs.otherCharacters.includes(c.id));

    try {
      // Get auth token for API calls
      const authToken = await getToken();

      // Generate the story with page structure
      const result = await generateStory({
        mainCharacter,
        otherCharacters,
        storytellingStyle: inputs.storytellingStyle,
        storyThemes: inputs.storyThemes,
        storySetting: inputs.storySetting,
        storyDetails: inputs.storyDetails,
        storyLength: inputs.storyLength,
        selectedVoice: inputs.selectedVoice,
        generatePages: true,
        authToken
      });

      setGeneratedTitle(result.title);
      setGeneratedStory(result.content);
      setGeneratedPages(result.pages || []);
      setSavedStoryId(null);
      savedStoryIdRef.current = null;
      setStep(prev => prev + 1);

      // Put the readable text on the user's shelf immediately. Images are
      // persisted into this same document as they finish below.
      const draftStoryId = await createDraftStory({
        title: result.title,
        content: result.content,
        pages: result.pages || [],
        mainCharacter,
      });

      // Start generating images in the background only when the user opted in.
      if (inputs.selectedImageModel !== 'none' && result.pages && result.pages.length > 0) {
        generateImagesForStory(result.pages, mainCharacter, otherCharacters, result.title, draftStoryId);
      }
    } catch (error) {
      console.error('[story-wizard] Story generation failed', error);
      setGeneratedStory("An error occurred while generating the story. Please try again.");
      setGeneratedTitle("Error");
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGeneratingStory(false);
      setGenerationStage('idle');
    }
  };

  // Generate images for story pages in background
  const generateImagesForStory = async (pages, mainChar, otherChars, storyTitle, draftStoryId = null) => {
    setIsGeneratingImages(true);
    setGenerationStage('images');
    let livePages = pages.map((page) => ({ ...page, imageStatus: page.imageStatus || 'pending' }));

    try {
      // Get auth token for API calls
      const authToken = await getToken();

      // Generate cover image first
      const coverResult = await generateCoverImage(
        storyTitle || generatedTitle || 'My Story',
        mainChar,
        inputs.storySetting,
        inputs.storyThemes,
        authToken,
        inputs.selectedImageModel
      );

      if (coverResult.success) {
        setCoverImageUrl(coverResult.imageUrl);
      }

      // Generate page images
      const allCharacters = [mainChar, ...otherChars].filter(Boolean);
      const imageResults = await generateStoryImages(
        pages,
        {
          characters: allCharacters,
          setting: inputs.storySetting,
          style: inputs.storytellingStyle,
          imageModel: inputs.selectedImageModel,
        },
        (_completed, _total, _status, batchResults = []) => {
          // Reveal each illustration in the in-wizard reader as soon as its
          // serverless batch returns instead of waiting for the whole book.
          livePages = livePages.map((page, index) => {
            const imageResult = batchResults.find((result) => result.pageIndex === index);
            if (!imageResult) return page;
            return imageResult.success
              ? { ...page, imageUrl: imageResult.imageUrl, imageStatus: 'completed' }
              : { ...page, imageStatus: 'failed' };
          });
          setGeneratedPages([...livePages]);
        },
        authToken
      );

      // Ensure the final state includes every result, including any response
      // that arrived between the last progress callback and completion.
      const completedPages = livePages.map((page, index) => {
        const imageResult = imageResults.find(r => r.pageIndex === index);
        if (imageResult && imageResult.success) {
          return { ...page, imageUrl: imageResult.imageUrl, imageStatus: 'completed' };
        }
        return page.imageUrl ? page : { ...page, imageStatus: 'failed' };
      });
      setGeneratedPages(completedPages);

      if (draftStoryId || savedStoryIdRef.current) {
        const persistedAssets = await persistStoryAssets(
          completedPages,
          coverResult.success ? coverResult.imageUrl : null,
          user.id,
          draftStoryId || savedStoryIdRef.current,
          authToken
        );
        await updateDoc(doc(db, 'stories', draftStoryId || savedStoryIdRef.current), {
          pages: persistedAssets.pages,
          cover_image_url: persistedAssets.coverImageUrl,
          images_status: completedPages.some(page => page.imageUrl) ? 'completed' : 'failed',
          generation_status: 'complete',
          updated_at: new Date().toISOString(),
        });
      }

      toast({
        title: "Illustrations Ready",
        description: "Your storybook illustrations have been created!",
      });
    } catch (error) {
      if (draftStoryId || savedStoryIdRef.current) {
        try {
          await updateDoc(doc(db, 'stories', draftStoryId || savedStoryIdRef.current), {
            images_status: 'failed',
            generation_status: 'complete',
            updated_at: new Date().toISOString(),
          });
        } catch (statusError) {
          console.warn('[story-wizard] Could not persist failed image status:', statusError.message);
        }
      }
      toast({
        title: "Image Generation",
        description: "Some illustrations couldn't be created. The story is still available.",
        variant: "default",
      });
      setSaveError(`Illustrations could not be stored: ${error.message || 'unknown storage error'}`);
    } finally {
      setIsGeneratingImages(false);
      setGenerationStage('idle');
    }
  };

  const handleSaveStory = async () => {
    if (!user) {
      setSaveError("You must be logged in to save a story.");
      return;
    }
    if (!generatedStory) {
      setSaveError("No story content to save.");
      return;
    }

    setIsSavingStory(true);
    setSaveError(null);
    setSaveSuccess(false);
    setSavedStoryId(null);

    const titleToSave = generatedTitle || "Untitled Story";
    const mainCharacter = characters.find(c => c.id === inputs.mainCharacter);

    try {
      const storyRef = doc(collection(db, "stories"));
      const authToken = await getToken();
      const persistedAssets = await persistStoryAssets(
        generatedPages,
        coverImageUrl,
        user.id,
        storyRef.id,
        authToken
      );
      await setDoc(storyRef, {
        user_id: user.id,
        author_name: authorName,
        title: titleToSave,
        story_content: generatedStory,
        pages: persistedAssets.pages.length > 0 ? persistedAssets.pages : null,
        cover_image_url: persistedAssets.coverImageUrl,
        themes: inputs.storyThemes,
        setting: inputs.storySetting,
        style: inputs.storytellingStyle,
        custom_details: inputs.storyDetails,
        selected_voice: inputs.selectedVoice,
        audio_page_urls: pageAudioUrls,
        image_model: inputs.selectedImageModel,
        main_character_id: mainCharacter?.id || null,
        is_public: false,
        generation_status: 'complete',
        images_status: inputs.selectedImageModel === 'none'
          ? 'disabled'
          : (isGeneratingImages ? 'generating' : (persistedAssets.pages.some(p => p.imageUrl) ? 'completed' : 'pending')),
        created_at: new Date().toISOString()
      });

      const data = { id: storyRef.id };

      if (data && data.id) {
        setSavedStoryId(data.id);
        savedStoryIdRef.current = data.id;
        setSaveSuccess(true);
        toast({
          title: "Story Saved",
          description: "Your storybook has been saved to your library!",
        });
      } else {
        setSaveError("Story saved, but couldn't get ID for sharing.");
      }

    } catch (error) {
      setSaveError(`Failed to save story: ${error.message}`);
    } finally {
      setIsSavingStory(false);
    }
  };

  // --- Share Handlers ---
  const getShareUrl = () => {
    if (savedStoryId) {
      // Construct the URL for the specific story page
      return `${window.location.origin}/story/${savedStoryId}`;
    } 
    // Potentially return a link to the wizard or a generic site link if story isn't saved?
    return window.location.href; // Or null or some default
  };
  
  const handleOpenShareDialog = () => {
    if (!savedStoryId) {
      setSaveError("Please save the story before sharing it.");
      return;
    }
    setIsShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setIsShareDialogOpen(false);
  };

  const CharacterSelection = useCallback(({ isMain = true }) => (
    <Box
      role={isMain ? 'radiogroup' : 'group'}
      aria-label={isMain ? 'Choose a main character' : 'Choose supporting characters'}
      sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}
    >
      <Grid container spacing={2} direction="column">
        {characters.map((character) => {
          const isSelected = isMain
            ? inputs.mainCharacter === character.id
            : inputs.otherCharacters.includes(character.id)
          const isDisabled = !isMain && inputs.mainCharacter === character.id
          return (
            <Grid item xs={12} key={character.id}>
              <Paper
                elevation={isSelected ? 3 : 1}
                role={isMain ? 'radio' : 'checkbox'}
                aria-checked={isSelected}
                aria-label={`${character.name}${isDisabled ? ' (already selected as main character)' : ''}`}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onClick={() => {
                  if (!isDisabled) {
                    isMain
                      ? handleMainCharacterSelect(character.id)
                      : handleOtherCharacterSelect(character.id)
                  }
                }}
                onKeyDown={(event) => {
                  if (!isDisabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    isMain
                      ? handleMainCharacterSelect(character.id)
                      : handleOtherCharacterSelect(character.id);
                  }
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  bgcolor: isSelected ? 'primary.light' : 'background.paper',
                  border: isSelected ? 2 : 0,
                  borderColor: isSelected ? 'primary.main' : 'transparent',
                  opacity: isDisabled ? 0.5 : 1,
                  '&:hover': {
                    bgcolor: isDisabled ? 'background.paper' : 'grey.100',
                  },
                  '&:focus-visible': {
                    outline: `3px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    src={character.avatar || "/placeholder.svg?height=200&width=200&text=😊"}
                    alt={`${character.name}`}
                    sx={{ width: 60, height: 60, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h6">{character.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {character.gender}, {character.age} years old
                    </Typography>
                    {character.traits && Array.isArray(character.traits) && character.traits.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Traits: {character.traits.join(', ')}
                      </Typography>
                    )}
                    {character.likes && Array.isArray(character.likes) && character.likes.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Likes: {character.likes.join(', ')}
                      </Typography>
                    )}
                    {character.dislikes && Array.isArray(character.dislikes) && character.dislikes.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Dislikes: {character.dislikes.join(', ')}
                      </Typography>
                    )}
                    {character.fears && Array.isArray(character.fears) && character.fears.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Fears: {character.fears.join(', ')}
                      </Typography>
                    )}
                    {character.goals && Array.isArray(character.goals) && character.goals.length > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Goals: {character.goals.join(', ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          )
        })}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            {isMain && !inputs.mainCharacter && (
              <Typography 
                variant="subtitle2" 
                color="error" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  padding: '8px 16px',
                  borderRadius: '4px',
                  width: 'fit-content',
                  fontStyle: 'italic'
                }}
              >
                Select a main character to continue
              </Typography>
            )}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setIsDialogOpen(true)}
              sx={{
                width: 'auto',
                justifyContent: 'center',
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.light,
                },
                px: 3,
                py: 1,
              }}
            >
              Add New Character
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  ), [characters, inputs.mainCharacter, inputs.otherCharacters, handleMainCharacterSelect, handleOtherCharacterSelect, setIsDialogOpen, theme.palette.primary])

  // Add these functions at the component level
  const allThemesLoaded = displayedThemeOptions.length === themeOptions.length;
  const allSettingsLoaded = displayedSettingOptions.length === settingOptions.length;
  const allStylesLoaded = displayedStyleOptions.length === styleOptions.length;

  // Add handler for randomization
  const handleRandomizeDetails = () => {
    const numThemes = Math.floor(Math.random() * 3) + 1;
    const randomThemeOptions = getRandomOptions(themeOptions, numThemes);
    const randomThemeValues = randomThemeOptions.map(opt => opt.value);
    handleInputChange("storyThemes", randomThemeValues);
    setDisplayedThemeOptions(themeOptions.slice(0, 5));

    const randomSettingOption = getRandomOptions(settingOptions, 1)[0];
    if (randomSettingOption) {
      handleInputChange("storySetting", randomSettingOption.value);
      setDisplayedSettingOptions(settingOptions.slice(0, 5));
    }

    const randomStyleOption = getRandomOptions(styleOptions, 1)[0];
    if (randomStyleOption) {
      handleInputChange("storytellingStyle", randomStyleOption.value);
      setDisplayedStyleOptions(styleOptions.slice(0, 5));
    }

    const randomLengthValue = Math.floor(Math.random() * 3);
    handleInputChange("storyLength", randomLengthValue);
  };

  // ADDED: Function to play voice preview
  const handlePlayVoicePreview = async (voiceId) => {
    if (!voiceId) {
      toast({ title: "Select a voice", description: "Please select a voice to preview.", variant: "default" });
      return;
    }
    // Find the voice object to get the display name for the preview
    const voiceDetails = availableVoices.find(v => v.id === voiceId);
    const voiceDisplayNameForPreview = voiceDetails ? voiceDetails.name : (voiceId.charAt(0).toUpperCase() + voiceId.slice(1));

    setIsPreviewLoading(true);
    setPreviewLoadingVoiceId(voiceId); // SETTING which voice is loading
    setPreviewError(null);

    // Revoke previous object URL if any
    if (audioPreviewRef.current && audioPreviewRef.current.src && audioPreviewRef.current.src.startsWith('blob:')) {
      URL.revokeObjectURL(audioPreviewRef.current.src);
    }

    try {
      // Capitalize the first letter of the voiceId for a more natural sentence
      // const voiceDisplayName = voiceId.charAt(0).toUpperCase() + voiceId.slice(1); (No longer needed directly like this)
      const sampleText = `Hello, I am ${voiceDisplayNameForPreview}, and this is a preview of how I sound.`;
      const authToken = await getToken();
      const audioContent = await generateAudio(sampleText, voiceId, authToken); // generateAudio is from ttsService
      
      const audioBlob = new Blob([audioContent], { type: 'audio/wav' });
      const objectUrl = URL.createObjectURL(audioBlob);
      
      if (audioPreviewRef.current) {
        audioPreviewRef.current.src = objectUrl;
        audioPreviewRef.current.play()
          .catch(() => {
            setPreviewError("Could not play preview. Please try again.");
            toast({ title: "Preview Error", description: "Could not play preview.", variant: "destructive" });
            URL.revokeObjectURL(objectUrl);
          });
      } else {
        throw new Error("Audio element for preview not available.");
      }
    } catch (error) {
      setPreviewError(error.message || "Failed to generate voice preview.");
      toast({ title: "Preview Error", description: error.message || "Failed to generate preview.", variant: "destructive" });
    } finally {
      setIsPreviewLoading(false);
      setPreviewLoadingVoiceId(null);
    }
  };

  const steps = [
    { title: "Choose Your Main Character", content: <CharacterSelection isMain={true} /> },
    { title: "Choose Other Characters", content: <CharacterSelection isMain={false} /> },
    {
      title: "Set Story Details",
      content: (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1.5, 
          width: '100%',
          p: 2,
          boxSizing: 'border-box'
        }}>
          <Autocomplete
            multiple
            id="themes-select"
            options={themeOptions}
            value={themeOptions.filter(option => inputs.storyThemes.includes(option.value))}
            getOptionLabel={(option) => option.label}
            onChange={(event, newValue) => {
              handleInputChange("storyThemes", newValue.map(option => option.value));
            }}
              renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select themes" 
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white'
                  }
                }}
              />
            )}
          />
          <Autocomplete
            id="setting-select"
            options={settingOptions}
            value={settingOptions.find(option => option.value === inputs.storySetting) || null}
            getOptionLabel={(option) => option.label}
            onChange={(event, newValue) => {
              handleInputChange("storySetting", newValue ? newValue.value : null);
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select setting" 
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white'
                  }
                }}
              />
            )}
          />
          <Autocomplete
            id="style-select"
            options={styleOptions}
            value={styleOptions.find(option => option.value === inputs.storytellingStyle) || null}
            getOptionLabel={(option) => option.label}
            onChange={(event, newValue) => {
              handleInputChange("storytellingStyle", newValue ? newValue.value : null);
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select storytelling style" 
                required
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'white'
                  }
                }}
              />
            )}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <TextField
              label="Additional Story Details"
              value={inputs.storyDetails}
              onChange={(e) => handleInputChange("storyDetails", e.target.value)}
              multiline
              rows={4}
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'white'
                }
              }}
            />
            <Button 
              onClick={generateDetailSuggestions}
              variant="outlined"
              disabled={isGeneratingSuggestions}
              startIcon={<CasinoIcon sx={{ transform: 'rotate(90deg)' }} />}
              sx={{ 
                alignSelf: 'flex-start',
                color: 'text.primary',
                borderColor: 'text.primary',
                backgroundColor: 'white',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'action.hover'
                },
                textTransform: 'none',
                fontWeight: 'normal',
                borderRadius: '8px',
                py: 1,
                px: 2
              }}
            >
              {isGeneratingSuggestions ? 'Generating...' : 'Suggest Details'}
            </Button>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>Story Length</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{valueToLengthText(inputs.storyLength)}</Typography>
            </Box>
            <ThemeProvider theme={theme}>
              <Slider
                value={typeof inputs.storyLength === 'number' ? inputs.storyLength : 1}
                onChange={(_, newValue) => handleInputChange("storyLength", newValue)}
                min={0}
                max={2}
                step={1}
                marks={lengthMarks}
                aria-label="Story Length"
              />
            </ThemeProvider>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              pt: 0.5,
              '& > span': {
                fontSize: '0.75rem',
                color: 'text.secondary'
              }
            }}>
              <span>Short</span>
              <span>Medium</span>
              <span>Long</span>
            </Box>
          </Box>
          <Button
            onClick={handleRandomizeDetails}
            variant="contained"
            startIcon={<CasinoIcon />}
            sx={{ 
              alignSelf: 'center',
              backgroundColor: '#9333EA',
              '&:hover': {
                backgroundColor: '#7E22CE'
              },
              px: 3,
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 'normal',
              fontSize: '0.875rem',
              mt: 1
            }}
          >
            Randomize Selections
          </Button>
        </Box>
      ),
    },
    {
      title: "Story Illustrations",
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose illustrations for a picture-book experience, or keep the story text-only for faster generation and no image cost.
          </Typography>
          <Box data-testid="illustration-options" role="radiogroup" aria-label="Illustration option" aria-required="true" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {imageModelOptions.map((model) => (
              <Paper
              data-testid={`illustration-option-${model.id}`}
              key={model.id}
              elevation={inputs.selectedImageModel === model.id ? 3 : 1}
              role="radio"
              aria-checked={inputs.selectedImageModel === model.id}
              aria-label={`${model.name}${model.recommended ? ' (recommended)' : ''}`}
              tabIndex={0}
              onClick={() => handleInputChange("selectedImageModel", model.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleInputChange("selectedImageModel", model.id);
                }
              }}
              sx={{
                p: 2,
                cursor: 'pointer',
                border: inputs.selectedImageModel === model.id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                backgroundColor: inputs.selectedImageModel === model.id ? theme.palette.action.selected : theme.palette.background.paper,
                transition: 'border 0.2s, background-color 0.2s, box-shadow 0.2s',
                position: 'relative',
                '&:hover': {
                  borderColor: theme.palette.primary.light,
                  boxShadow: `0px 0px 8px ${theme.palette.primary.light}60`,
                }
                , '&:focus-visible': {
                  outline: `3px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                }
              }}
            >
              {model.recommended && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="primary"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: inputs.selectedImageModel === model.id ? 'bold' : 'medium' }}>
                  {model.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {model.description}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {model.features.map((feature, idx) => (
                    <Chip
                      key={idx}
                      label={feature}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  {model.id === 'none' ? 'No image generation cost' : `Est. cost: ~$${model.costPerImage.toFixed(4)} per image`}
                </Typography>
              </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      ),
    },
    {
      title: "Audio Options",
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={inputs.audioEnabled}
                  onChange={(e) => handleInputChange("audioEnabled", e.target.checked)}
                  size="small"
                  disabled={!canUseAudio}
                />
              }
              label="Enable audio narration"
            />
            {!canUseAudio && (
              <Chip
                label="Premium"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
          </Box>
          {!canUseAudio && (
            <Typography variant="caption" color="text.secondary">
              Audio narration is available on Premium and above.{' '}
              <a href="/pricing" style={{ color: 'inherit', textDecoration: 'underline' }}>Upgrade →</a>
            </Typography>
          )}
          {inputs.audioEnabled && (
            <Box role="radiogroup" aria-label="Narrator voice" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
              {availableVoices.map((voice) => (
                <Paper 
                  key={voice.id} 
                  elevation={inputs.selectedVoice === voice.id ? 3 : 1}
                  role="radio"
                  aria-checked={inputs.selectedVoice === voice.id}
                  aria-label={`${voice.name} voice`}
                  tabIndex={0}
                  onClick={() => handleInputChange("selectedVoice", voice.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleInputChange("selectedVoice", voice.id);
                    }
                  }}
                  sx={{
                    p: 1.5,
                    cursor: 'pointer',
                    border: inputs.selectedVoice === voice.id ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                    backgroundColor: inputs.selectedVoice === voice.id ? theme.palette.action.selected : theme.palette.background.paper,
                    transition: 'border 0.2s, background-color 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.light,
                      boxShadow: `0px 0px 8px ${theme.palette.primary.light}60`,
                    }
                    , '&:focus-visible': {
                      outline: `3px solid ${theme.palette.primary.main}`,
                      outlineOffset: 2,
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: inputs.selectedVoice === voice.id ? 'bold' : 'normal' }}>
                        {voice.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'normal' }}>
                        {voice.gender} - {voice.description}
                      </Typography>
                    </Box>
                    <Tooltip title={`Preview ${voice.name}`}>
                      <span>
                        <IconButton 
                          onClick={(e) => { 
                            e.stopPropagation(); // Prevent Paper onClick from firing
                            handlePlayVoicePreview(voice.id); 
                          }}
                          aria-label={`Preview ${voice.name} voice`}
                          disabled={isPreviewLoading}
                          size="medium" 
                          sx={{ ml: 1 }}
                        >
                          {(isPreviewLoading && previewLoadingVoiceId === voice.id) ? <CircularProgress size={24} /> : <Volume2 />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          {previewError && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {previewError}
            </Typography>
          )}
          {/* ADDED Hidden audio element for previews */}
          <audio ref={audioPreviewRef} style={{ display: 'none' }} 
            onEnded={() => {
              if (audioPreviewRef.current && audioPreviewRef.current.src && audioPreviewRef.current.src.startsWith('blob:')) {
                URL.revokeObjectURL(audioPreviewRef.current.src);
              }
            }}
            onError={() => {
               if (audioPreviewRef.current && audioPreviewRef.current.src && audioPreviewRef.current.src.startsWith('blob:')) {
                URL.revokeObjectURL(audioPreviewRef.current.src);
              }
              setPreviewError("Error during audio playback.");
            }}
          />
        </Box>
      ),
    },
    {
      title: "Review",
      content: (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2"><strong>Main Character:</strong> {characters.find(c => c.id === inputs.mainCharacter)?.name || 'Not selected'}</Typography>
          <Typography variant="body2"><strong>Other Characters:</strong> {inputs.otherCharacters.map(id => characters.find(c => c.id === id)?.name).filter(Boolean).join(", ") || 'None'}</Typography>
          <Typography variant="body2"><strong>Story Length:</strong> {valueToLengthText(inputs.storyLength)}</Typography>
          <Typography variant="body2"><strong>Themes:</strong> {inputs.storyThemes.join(", ") || 'Not selected'}</Typography>
          <Typography variant="body2"><strong>Setting:</strong> {inputs.storySetting || 'Not selected'}</Typography>
          <Typography variant="body2"><strong>Storytelling Style:</strong> {inputs.storytellingStyle || 'Not selected'}</Typography>
          <Typography variant="body2"><strong>Additional Details:</strong> {inputs.storyDetails || 'None'}</Typography>
          <Typography variant="body2"><strong>Audio Narration:</strong> {inputs.audioEnabled ? "Enabled" : "Disabled"}</Typography>
          {inputs.audioEnabled &&
            <Typography variant="body2">
              <strong>Narrator Voice:</strong> {availableVoices.find(v => v.id === inputs.selectedVoice)?.name || inputs.selectedVoice}
            </Typography>}
          <Typography variant="body2">
            <strong>Illustrations:</strong> {imageModelOptions.find(m => m.id === inputs.selectedImageModel)?.name || 'Choose an option'}
          </Typography>
        </Box>
      ),
    },
    {
      title: "Your Storybook",
      content: (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: '100%', height: '100%' }}>
          {isGeneratingStory ? (
            <Box role="status" aria-live="polite" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                {generationStage === 'images' ? 'Painting your illustrations…' :
                 generationStage === 'audio' ? 'Recording the narration…' :
                 'Writing your story…'}
              </Typography>
              <Box
                role="progressbar"
                aria-label="Story generation progress"
                aria-valuemin={0}
                aria-valuemax={3}
                aria-valuenow={generationStage === 'audio' ? 3 : generationStage === 'images' ? 2 : 1}
                sx={{ display: 'flex', gap: 0.75, mt: 1 }}
              >
                {['text', 'images', 'audio'].map((stage) => {
                  const order = { text: 0, images: 1, audio: 2 };
                  const current = order[generationStage] ?? 0;
                  const done = order[stage] < current;
                  const active = stage === generationStage;
                  return (
                    <Box key={stage} sx={{
                      width: 28, height: 4, borderRadius: 2,
                      bgcolor: done ? 'primary.main' : active ? 'primary.light' : 'grey.300',
                      transition: 'background-color 200ms',
                    }} />
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary">
                This usually takes about a minute.
              </Typography>
            </Box>
          ) : generatedPages.length > 0 ? (
            <Storybook3DReader
              title={generatedTitle || "Untitled Story"}
              author={authorName}
              pages={generatedPages}
              coverImageUrl={coverImageUrl}
              isGeneratingImages={isGeneratingImages}
              mainCharacter={characters.find(c => c.id === inputs.mainCharacter)}
              showControls={true}
              narration={{
                mode: inputs.audioEnabled ? 'tts' : 'browser',
                voice: inputs.selectedVoice,
                storyId: savedStoryId || savedStoryIdRef.current,
                pageAudioUrls: pageAudioUrls[inputs.selectedVoice] || {},
                onPageAudioStored: handlePageAudioStored,
              }}
            />
          ) : generatedStory ? (
            // Fallback for stories without pages
            <Box>
              <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center' }}>
                {generatedTitle || "Untitled Story"}
              </Typography>
              {inputs.audioEnabled ? (
                <StoryAudioPlayer storyText={generatedStory} voice={inputs.selectedVoice} />
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", p: 2 }}>
                  {generatedStory}
                </Typography>
              )}
            </Box>
          ) : null}

          <Box sx={{ flexShrink: 0, textAlign: 'center', pt: 0.5 }}>
            {saveError && <Typography color="error" variant="caption">{saveError}</Typography>}
            {saveSuccess && <Typography color="success.main" variant="caption">Storybook Saved!</Typography>}
          </Box>
        </Box>
      ),
    },
  ]

  // Ensure step is within bounds
  const currentStep = steps[step] || steps[0];


  // Check if we're on the final storybook step
  const isStorybookStep = step === steps.length - 1 && generatedPages.length > 0;

  return (
    <Paper data-testid="story-wizard" elevation={3} sx={{
      width: '100%',
      maxWidth: isStorybookStep
        ? { xs: '100%', sm: '100%', md: '900px', lg: '1000px' }
        : { xs: '100%', sm: '540px', md: '600px' },
      maxHeight: isStorybookStep
        ? { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 100px)' }
        : { xs: 'calc(100vh - 100px)', sm: 'calc(100vh - 140px)' },
      bgcolor: 'background.paper',
      borderRadius: { xs: 0, sm: 2 },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      mt: { xs: 0, sm: 2, md: 4 },
      mb: 2,
      mx: 'auto',
      transition: 'max-width 0.3s ease',
    }}>
      <Box sx={{ 
        p: 2, pb: 1, flexShrink: 0, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="subtitle2" color="primary">
            {currentStep.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Step {step + 1} of {steps.length}
          </Typography>
        </Box>
        <Box>
          {step === steps.length - 1 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                startIcon={<BookOpen />} 
                variant="contained" 
                color="primary"
                onClick={handleSaveStory} 
                disabled={isSavingStory || saveSuccess || !generatedStory || isGeneratingImages}
                size="small"
              >
                {isSavingStory ? "Saving..." : isGeneratingImages ? "Waiting for images..." : saveSuccess ? "Saved" : "Save Story"}
              </Button>
              <Tooltip title={!savedStoryId ? "Save Story First" : ""} arrow>
                <span>
                  <Button 
                    startIcon={<Share2 />} 
                    variant="outlined" 
                    onClick={handleOpenShareDialog}
                    disabled={!savedStoryId} 
                    size="small"
                  >
                    Share Story
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={Math.min(100, (step / Math.max(1, steps.length - 1)) * 100)}
        aria-label="Story creation progress"
        sx={{ height: 3 }}
      />

      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'auto', 
        px: 2, 
        py: 1, 
        display: 'flex',
        width: '100%',
        boxSizing: 'border-box',
        '& .MuiBox-root': {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }
      }}>
        {React.cloneElement(currentStep.content, { key: step })}
      </Box>

      <Box sx={{ 
        p: 2, pt: 1, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0, 
        gap: 1 
      }}>
        {step < steps.length - 1 ? (
          <>
            <Button 
              onClick={() => setStep(Math.max(0, step - 1))} 
              disabled={step === 0}
              startIcon={<ChevronLeft />}
              size="small"
              sx={{ visibility: step === 0 ? 'hidden' : 'visible' }}
            >
              Back
            </Button>
            {step < steps.length - 2 ? (
              <Button 
                onClick={() => {
                  if (step === 0 && !inputs.mainCharacter) {
                    toast({
                      title: "Select a main character",
                      variant: "destructive",
                    });
                    return;
                  }
                  if (step === 2 && (!inputs.storyThemes.length || !inputs.storySetting || !inputs.storytellingStyle)) {
                    toast({
                      title: "Complete the story details",
                      description: "Choose at least one theme, a setting, and a storytelling style before continuing.",
                      variant: "destructive",
                    });
                    return;
                  }
                  if (step === 3 && !inputs.selectedImageModel) {
                    toast({
                      title: "Choose an illustration option",
                      description: "Select Text only, Fast illustrations, or Rich illustrations before continuing.",
                      variant: "destructive",
                    });
                    return;
                  }
                  setStep(step + 1);
                }}
                variant="contained"
                endIcon={<ChevronRight />}
                size="small"
              >
                Next
              </Button>
            ) : (
              <Button 
                onClick={handleGenerateStory}
                variant="contained"
                endIcon={<ChevronRight />}
                size="small"
                disabled={isGeneratingStory || firstIncompleteStep !== null}
              >
                {isGeneratingStory ? 'Generating...' : 'Generate Story'}
              </Button>
            )}
          </>
        ) : null}
      </Box>

      <NewCharacterDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleNewCharacterSubmit}
        newCharacter={newCharacter}
        onNewCharacterChange={handleNewCharacterChange}
        toast={toast}
        getToken={getToken}
        isAdmin={isAdmin}
      />
      <Dialog 
        open={isSuggestionDialogOpen} 
        onClose={() => setIsSuggestionDialogOpen(false)} 
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '16px',
            maxWidth: '600px',
            width: '90%',
          },
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '1.5rem', 
          fontWeight: 'bold',
          color: theme.palette.primary.main,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
          mb: 2
        }}>
          Story Detail Suggestions
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Click on a suggestion to add it to your story details:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, listStyle: 'none', padding: 0 }}>
            {detailSuggestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                onClick={() => {
                  handleInputChange("storyDetails", (inputs.storyDetails + " " + suggestion).trim());
                  setIsSuggestionDialogOpen(false);
                }}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  transition: 'all 0.3s ease',
                  height: 'auto',
                  '& .MuiChip-label': {
                    display: 'block',
                    whiteSpace: 'normal',
                    padding: '8px 12px',
                    textAlign: 'left',
                    width: '100%',
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={generateDetailSuggestions} 
            disabled={isGeneratingSuggestions}
            variant="contained"
            color="primary"
          >
            {isGeneratingSuggestions ? 'Regenerating...' : 'Regenerate'}
          </Button>
          <Button 
            onClick={() => setIsSuggestionDialogOpen(false)} 
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ShareDialog 
        open={isShareDialogOpen} 
        onClose={handleCloseShareDialog} 
        title={generatedTitle || `Story about ${characters.find(c => c.id === inputs.mainCharacter)?.name}` || 'My Story'} 
        storyUrl={getShareUrl()} 
        isOwnStory={true}
      />
    </Paper>
  )
}
